const _ = require("lodash");
const moment = require("moment");
const Big = require("big.js");
const {setIn} = require("../../../shared/ImmHelpers");
const StoreBundleUtil = require("../../../shared/StoreBundleUtil");
const LoadState = require("../../core/vault/util/LoadState");

/*
Payments shape {
  credit,
  upcoming: {
    load,
    loadError,
    payments: Array of Payment objects,
  },
  past: {
    nextLoad: {load, loadError, limit, offset},
    payments: Array of Payment objects,
  },
}
Payment shape {
  date,
  items: Array of {name, price},
  subtotal,
  credit,
  tax,
  total,
}
*/

const PAST_BATCH_SIZE = 10;

const init = function() {
	return {
		credit: 0,
		upcoming: LoadState.loadInit({
			payments: [],
		}),
		past: {
			nextLoad: LoadState.loadInit({limit: PAST_BATCH_SIZE, offset: 0}),
			payments: [],
		},
	};
};

const setCredit = function(ppay, value) {
	return setIn(ppay, ["credit"], value);
};

const getUpcomingPrice = function(ppay, subscriptionID) {
	subscriptionID = String(subscriptionID);
	if (ppay.upcoming && ppay.upcoming.payments) {
		let sub;
		_.each(ppay.upcoming.payments, p => {
			sub = _.find(p.items, s => s.subscriptionID === subscriptionID);
			if (sub) return false; // Short-circuits _.each.
		});
		return sub ? sub.price : null;
	}
	return null;
};

const resetUpcoming = function(ppay) {
	return setIn(ppay, ["upcoming"], LoadState.loadInit({payments: []}));
};

const loadUpcomingStart = function(ppay, api) {
	const requestLoad = () => api.loadUpcomingPayments();
	return setIn(ppay, ["upcoming"], ppayU =>
		LoadState.loadStart(ppayU, requestLoad)
	);
};

const loadUpcomingSuccess = function(ppay, rawData) {
	const byDate = _.groupBy(rawData.payments, s =>
		moment.utc(s.expiresAt).format("YYYY-MM-DD")
	);
	const payments = _.sortBy(_.keys(byDate)).map(date =>
		convertUpcomingPayment(byDate[date])
	);
	ppay = setIn(ppay, ["upcoming"], u => {
		u = LoadState.loadSuccess(u);
		u = setIn(u, ["payments"], payments);
		return u;
	});
	return ppay;
};

const makeFakeUpcomingItem = function(subscription) {
	// NOTE: Upcoming payments never include bundles, so this case probably never
	// applies. But it was already coded in an old draft so...
	const {planType, displayName, grade = 3} = subscription;
	const isBundle = planType === "bundle";
	const items = isBundle
		? StoreBundleUtil.makeItems(grade, displayName)
		: [
				{
					sku: "beast-" + planType,
					quantity: 1,
					options: {
						displayName,
					},
				},
		  ];
	items[0].subscriptionID = String(subscription.id);
	items[0].price = subscription.subtotal;
	return items;
};

// Computes the frontend payments array given the backend format for upcoming.
const convertUpcomingPayment = function(payments) {
	const date = moment.utc(payments[0].expiresAt).format("MMMM D, YYYY");
	const fakeSubscriptions = payments.map(p => ({
		id: p.id,
		planType: p.planType,
		displayName: p.studentName,
		subscriptionID: p.id,
		subtotal: p.subtotal,
	}));
	const initObj = {
		date,
		items: _.flatMap(fakeSubscriptions, makeFakeUpcomingItem),
		subtotal: Big(0),
		couponApplied: Big(0),
		shipping: Big(0),
		tax: Big(0),
		total: Big(0),
	};
	const nearFinalObj = payments.reduce((obj, payment) => {
		obj.subtotal = obj.subtotal.add(payment.subtotal);
		obj.couponApplied = obj.couponApplied.add(payment.couponApplied);
		obj.tax = obj.tax.add(payment.tax);
		obj.total = obj.total.add(payment.total);
		return obj;
	}, initObj);
	return _.mapValues(nearFinalObj, v => {
		return v instanceof Big ? v.toFixed(2) : v;
	});
};

const loadUpcomingFail = function(ppay, error) {
	return setIn(ppay, ["upcoming"], ppayU => LoadState.loadError(ppayU, error));
};

const loadMorePastStart = function(ppay, api) {
	if (!ppay.past.nextLoad || ppay.past.nextLoad.status === "done") return ppay;
	return setIn(ppay, ["past", "nextLoad"], ppayP => {
		const requestLoad = () => api.loadPastPayments(ppayP.limit, ppayP.offset);
		return LoadState.loadStart(ppayP, requestLoad);
	});
};

const loadMorePastSuccess = function(ppay, limit, offset, rawData) {
	const rawOrders = rawData.orders;
	if (rawOrders.length) {
		ppay = setIn(ppay, ["past", "payments"], list => {
			list = list.slice(0);
			_.range(limit).forEach(i => {
				if (rawOrders[i]) list[offset + i] = convertPastPayment(rawOrders[i]);
			});
			return list;
		});
	}

	if (rawOrders.length < limit) {
		ppay = setIn(ppay, ["past", "nextLoad"], LoadState.loadSuccess);
	} else {
		const nextLoad = LoadState.loadInit({limit, offset: offset + limit});
		ppay = setIn(ppay, ["past", "nextLoad"], nextLoad);
	}
	return ppay;
};

// Computes the frontend payments array given the backend format for past.
const convertPastPayment = function(order) {
	const converted = {
		orderID: order.id,
		couponCode: order.payment.couponCode,
		date: moment(order.date).format("MMMM D, YYYY"),
		items: order.items,
		subtotal: order.payment.subtotal,
		shipping: order.payment.shipping,
		tax: order.payment.tax,
		couponApplied: order.payment.coupon,
		total: order.payment.total,
	};
	const {type, previousType, newType, newIsBundle, displayName} = _.get(
		order,
		"subscriptionHistory.0",
		{}
	);
	const isUpgrade = type === "upgrade";
	if (isUpgrade) {
		converted.upgradeInfo = {
			previousType,
			newType: newIsBundle ? "bundle" : newType,
			displayName,
		};
	}
	return converted;
};

const loadMorePastFail = function(ppay, error) {
	ppay = setIn(ppay, ["past", "nextLoad"], ppayP =>
		LoadState.loadError(ppayP, error)
	);
	return ppay;
};

module.exports = {
	init,
	setCredit,
	getUpcomingPrice,
	resetUpcoming,
	loadUpcomingStart,
	loadUpcomingSuccess,
	loadUpcomingFail,
	loadMorePastStart,
	loadMorePastSuccess,
	loadMorePastFail,
};
